/**
 * BuildingOrientation — the compass-grid orientation picker, LIFTED verbatim from
 * `PurchaseFlow/common/BuildingOrientation` (presentational, design-system only)
 * so the native module carries NO `PurchaseFlow/*` import. Uses the
 * `constants/purchaseFlow` `OrientationType` (§2 divergence: the legacy view
 * imported a duplicate enum from `PurchaseFlow/common` — the member lists are
 * byte-identical, so the constants copy is used here).
 */

import React from 'react'
import { Box, Stack } from '@mui/material'

import { OrientationType } from 'constants/purchaseFlow'
import { BLUE_600 } from 'constants/styling/theme'
import { Label } from 'components/common/Label'
import { ToggleButton } from 'components/common/ToggleButton'
import { useTranslation } from 'react-i18next'

interface Props {
  onClick: (orientation: OrientationType) => void
  type: OrientationType
}

const TOP = 'top'
const MIDDLE = 'middle'
const BOTTOM = 'bottom'

export const BuildingOrientation: React.FC<Props> = ({ onClick, type }) => {
  const { t } = useTranslation(['purchase_flow'])

  const orientations = [
    { type: OrientationType.NORTH_WEST, row: TOP },
    { type: OrientationType.NORTH, row: TOP },
    { type: OrientationType.NORTH_EAST, row: TOP },
    { type: OrientationType.WEST, row: MIDDLE },
    { type: OrientationType.EAST, row: MIDDLE },
    { type: OrientationType.SOUTH_WEST, row: BOTTOM },
    { type: OrientationType.SOUTH, row: BOTTOM },
    { type: OrientationType.SOUTH_EAST, row: BOTTOM },
  ]

  const renderToggleButton = (orientation: { type: OrientationType; row: string }) => {
    const isFullWidth = [OrientationType.WEST, OrientationType.NORTH, OrientationType.SOUTH, OrientationType.EAST].includes(orientation.type)
    return (
      <ToggleButton
        key={orientation.type}
        isSelected={type === orientation.type}
        label={t(`orientation_type.${orientation.type}`)}
        sx={{
          width: isFullWidth ? '6.4rem' : '4.6rem',
          height: '4rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(orientation.row === MIDDLE && { flexShrink: 0 }),
        }}
        onClick={() => onClick?.(orientation.type)}
        typographyVariant="text-md"
      />
    )
  }

  const OrientationRow = ({ orientations: rowOrientations }: { orientations: { type: OrientationType; row: string }[] }) => (
    <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'center', width: '100%' }}>{rowOrientations.map(renderToggleButton)}</Box>
  )

  return (
    <Stack gap={1}>

      <Label text={t('orientation_title')} />

      <Stack flexDirection="row" flexWrap="wrap" justifyContent="center">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', gap: '1rem' }}>

          <OrientationRow orientations={orientations.filter((o) => o.row === TOP)} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>

            {renderToggleButton({ type: OrientationType.WEST, row: MIDDLE })}

            <Box sx={{ width: '2.8rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ width: '.7rem', height: '.7rem', borderRadius: '50%', backgroundColor: BLUE_600 }} />
            </Box>

            {renderToggleButton({ type: OrientationType.EAST, row: MIDDLE })}

          </Box>

          <OrientationRow orientations={orientations.filter((o) => o.row === BOTTOM)} />

        </Box>
      </Stack>

    </Stack>
  )
}
